import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

const StatusUpdateModal = ({ order, language, onClose, onUpdate }) => {
  const [newStatus, setNewStatus] = useState(order?.status);
  const [note, setNote] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const labels = {
    title: { en: 'Update Order Status', ar: 'تحديث حالة الطلب' },
    orderNumber: { en: 'Order Number', ar: 'رقم الطلب' },
    currentStatus: { en: 'Current Status', ar: 'الحالة الحالية' },
    newStatus: { en: 'New Status', ar: 'الحالة الجديدة' },
    selectStatus: { en: 'Select Status', ar: 'اختر الحالة' },
    pending: { en: 'Pending', ar: 'قيد الانتظار' },
    inProgress: { en: 'In Progress', ar: 'قيد التنفيذ' },
    completed: { en: 'Completed', ar: 'مكتمل' },
    cancelled: { en: 'Cancelled', ar: 'ملغي' },
    addNote: { en: 'Add Note (Optional)', ar: 'إضافة ملاحظة (اختياري)' },
    notePlaceholder: { en: 'Enter notes about this status change...', ar: 'أدخل ملاحظات حول تغيير الحالة...' },
    cancel: { en: 'Cancel', ar: 'إلغاء' },
    update: { en: 'Update Status', ar: 'تحديث الحالة' },
    confirm: { en: 'Confirm', ar: 'تأكيد' },
    confirmMessage: { en: 'Are you sure you want to update the order status?', ar: 'هل أنت متأكد من تحديث حالة الطلب؟' },
    warning: { en: 'This action will be logged in the order timeline.', ar: 'سيتم تسجيل هذا الإجراء في سجل الطلب.' }
  };

  const statusOptions = [
    { value: 'Pending', label: labels?.pending?.[language] },
    { value: 'In Progress', label: labels?.inProgress?.[language] },
    { value: 'Completed', label: labels?.completed?.[language] },
    { value: 'Cancelled', label: labels?.cancelled?.[language] }
  ];

  const handleSubmit = () => {
    if (newStatus === order?.status && !note) {
      onClose();
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    onUpdate(order?.id, newStatus, note);
    setShowConfirm(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`bg-gray-900 border border-[#FFD700]/30 rounded-lg w-full max-w-2xl ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Header */}
        <div className="border-b border-[#FFD700]/20 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#FFD700]">{labels?.title?.[language]}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {!showConfirm ? (
          <div className="p-6 space-y-6">
            {/* Order Info */}
            <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-1">{labels?.orderNumber?.[language]}</p>
              <p className="text-white font-bold text-lg">{order?.id}</p>
            </div>

            {/* Current Status */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">{labels?.currentStatus?.[language]}</label>
              <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-3">
                <p className="text-white font-medium">{order?.status}</p>
              </div>
            </div>

            {/* New Status */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">{labels?.newStatus?.[language]}</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e?.target?.value)}
                className="w-full px-4 py-3 bg-black border border-[#FFD700]/30 rounded-lg text-white focus:outline-none focus:border-[#FFD700]"
              >
                {statusOptions?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Note */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">{labels?.addNote?.[language]}</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e?.target?.value)}
                placeholder={labels?.notePlaceholder?.[language]}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 bg-black border border-[#FFD700]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] resize-none"
              />
              <p className="text-gray-500 text-xs mt-1 text-right">{note?.length}/500</p>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Confirmation Message */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-2">{labels?.confirmMessage?.[language]}</p>
                  <p className="text-gray-400 text-sm">{labels?.warning?.[language]}</p>
                </div>
              </div>
            </div>

            {/* Status Change Summary */}
            <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-gray-400 text-sm">{labels?.orderNumber?.[language]}</p>
                <p className="text-white font-bold">{order?.id}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-gray-400 text-sm">{labels?.currentStatus?.[language]}</p>
                  <p className="text-white">{order?.status}</p>
                </div>
                <div className="text-[#FFD700]">→</div>
                <div className="flex-1">
                  <p className="text-gray-400 text-sm">{labels?.newStatus?.[language]}</p>
                  <p className="text-[#FFD700] font-bold">{newStatus}</p>
                </div>
              </div>
              {note && (
                <div>
                  <p className="text-gray-400 text-sm mb-1">{labels?.addNote?.[language]}</p>
                  <p className="text-gray-300 text-sm">{note}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-[#FFD700]/20 p-6 flex gap-3">
          <button
            onClick={showConfirm ? () => setShowConfirm(false) : onClose}
            className="flex-1 px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            {labels?.cancel?.[language]}
          </button>
          <button
            onClick={showConfirm ? handleConfirm : handleSubmit}
            className="flex-1 px-6 py-3 bg-[#FFD700] text-black rounded-lg hover:bg-[#FFD700]/90 transition-colors font-bold"
          >
            {showConfirm ? labels?.confirm?.[language] : labels?.update?.[language]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal;